/* engine/frontLayout.js — FRONT LAYOUT VARIANTS (Phase 2, 2026-07-30)
 *
 * The Cabinet Technician declares selectable front layouts on a unit:
 *
 *   u.frontLayouts = [{
 *     id,                       stable id ("fl-1") — cfg.frontLayout references it
 *     label,                    customer-facing name ("1 drawer + door")
 *     price,                    absolute € delta when selected (0/absent = free)
 *     rows: [{                  listed TOP → BOTTOM (how an elevation is read)
 *       kind,                   "door" | "drawer" | "open" (open = no front, niche)
 *       hMm,                    fixed row height in mm; absent/0 = FLEX row
 *       doors,                  door rows only: 1 | 2 leaves (default 1)
 *       hinge,                  1-leaf door rows: "L" | "R" (default: unit hinge)
 *     }]
 *   }]
 *
 * The selected layout REPLACES the unit's door/drawer front geometry with the
 * row stack — one normalization here, consumed by orbit 3D (cabinetMath),
 * Present (ProcCabinet), the elevation views and the drawings SVG, so every
 * surface shows the SAME physical fronts.
 *
 * Height algebra (def.height = H metres, cut-local y: 0 = body bottom):
 *   · fixed rows get their true mm (clamped to the body);
 *   · flex rows share the remaining height evenly (min 60 mm each);
 *   · fixed overflow scales the fixed rows down so flex rows keep the minimum;
 *   · an ALL-fixed stack scales proportionally to fill H exactly (a layout is
 *     a set of proportions when nothing can flex);
 *   · the bottom row absorbs float dust so the stack always closes at y = 0.
 */

export const LAYOUT_ROW_KINDS = ["door", "drawer", "open"];
const MIN_ROW_M = 0.06;

/** The layout object selected by cfg.frontLayout, or null (Standard/unknown). */
export function activeFrontLayout(def, config) {
  const id = config && config.frontLayout;
  if (!id) return null;
  const L = ((def && def.frontLayouts) || []).find(
    (l) => l && String(l.id) === String(id) && Array.isArray(l.rows) && l.rows.length
  );
  return L || null;
}

/** Normalized row stack for the selected layout, or null → legacy front path.
 *  Rows come back TOP→BOTTOM with resolved heights:
 *    { kind, y0, y1,   metres in cut-local space (0 = body bottom, H = top)
 *      f0, f1,         fractions of H (renderers with a different body band)
 *      doors, hinge }  door rows: leaf count 1|2, explicit hinge or null   */
export function frontRowsOf(def, config) {
  const L = activeFrontLayout(def, config);
  const H = def && Number(def.height);
  if (!L || !(H > 0)) return null;
  const rows = L.rows.filter((r) => r && LAYOUT_ROW_KINDS.indexOf(r.kind) >= 0);
  if (!rows.length) return null;

  const fixed = rows.map((r) => {
    const mm = Number(r.hMm);
    return Number.isFinite(mm) && mm >= 40 ? Math.min(mm / 1000, H) : null;
  });
  const nFlex = fixed.filter((v) => v == null).length;
  let sumFixed = fixed.reduce((s, v) => s + (v || 0), 0);
  if (nFlex === 0) {
    // all-fixed → proportional fill (the stack always spans the body)
    const k = sumFixed > 0 ? H / sumFixed : 1;
    for (let i = 0; i < fixed.length; i++) fixed[i] = (fixed[i] || 0) * k;
    sumFixed = H;
  } else if (sumFixed > H - nFlex * MIN_ROW_M) {
    // fixed overflow → shrink fixed rows so every flex row keeps its minimum
    const target = Math.max(H - nFlex * MIN_ROW_M, H * 0.2);
    const k = sumFixed > 0 ? target / sumFixed : 1;
    for (let i = 0; i < fixed.length; i++) if (fixed[i] != null) fixed[i] *= k;
    sumFixed = target;
  }
  const flexH = nFlex ? Math.max(MIN_ROW_M, (H - sumFixed) / nFlex) : 0;

  const out = [];
  let yTop = H;
  rows.forEach((r, i) => {
    const h = fixed[i] != null ? fixed[i] : flexH;
    const y1 = yTop;
    const y0 = Math.max(0, yTop - h);
    yTop = y0;
    out.push({
      kind: r.kind,
      y0, y1, f0: y0 / H, f1: y1 / H,
      doors: r.kind === "door" ? Math.max(1, Math.min(2, parseInt(r.doors, 10) || 1)) : 0,
      hinge: r.hinge === "L" || r.hinge === "R" ? r.hinge : null,
    });
  });
  const last = out[out.length - 1];
  last.y0 = 0; last.f0 = 0;                       // close the stack at the body bottom
  return out;
}

/** Human summary for resolver reasons / the assembly inspector:
 *  "drawer 140 / drawer 140 / door" */
export function layoutRowsSummary(rows) {
  return (rows || [])
    .map((r) => r.kind + (r.kind === "door" && r.doors > 1 ? "×2" : "") +
      " " + Math.round((r.y1 - r.y0) * 1000))
    .join(" / ");
}
